import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { createUserTeamWithLineup, editUserTeamWithLineup } from '../../api/userTeams';
import { fetchCardsByUserIDPaginated, fetchUserCardTeams } from '../../api/cards';
import { toast } from 'react-toastify';
import PositionPickerModal from '../../components/PositionPickerModal';
import Card from '../../components/Card';
import BackButton from '../../components/BackButton';
import TeamBuilderFilters from '../../components/TeamBuilderFilters';
import { getEligibleCards } from '../../api/constraints';
import { getLastTeamAbbrev } from '../../helpers/teamHelpers';
import useSeries from '../../hooks/useSeries';
import useConfig from '../../hooks/useConfig';

const POSITION_ROWS = [
    { label: 'Outfield', slots: [{ pos: 'LF' }, { pos: 'CF' }, { pos: 'RF' }] },
    { label: 'Infield', slots: [{ pos: '3B' }, { pos: 'SS' }, { pos: '2B' }, { pos: '1B' }] },
    { label: 'DH / Catcher', slots: [{ pos: 'DH' }, { pos: 'C' }] },
    { label: 'Bench', slots: [0, 1, 2, 3, 4].map(i => ({ pos: 'Bench', idx: i })) },
    { label: 'Starting Pitchers', slots: [0, 1, 2, 3, 4].map(i => ({ pos: 'SP', idx: i })) },
    { label: 'Relief Pitchers', slots: [0, 1, 2, 3, 4].map(i => ({ pos: 'RP', idx: i })) },
];

const BATTING_POSITIONS = ['C', '1B', '2B', '3B', 'SS', 'LF', 'CF', 'RF', 'DH'];
const FILL_ORDER = ['SS', '2B', 'C', 'CF', 'RF', 'LF', '3B', '1B', 'DH', 'SP', 'RP', 'Bench'];
const ALLOWED_FIELDS = new Set(['C', '1B', '2B', '3B', 'SS', 'LF', 'CF', 'RF', 'DH', 'SP', 'RP', 'Bench']);

const createEmptyLineup = () => ({
    C: null, '1B': null, '2B': null, '3B': null, SS: null,
    LF: null, CF: null, RF: null, DH: null,
    Bench: [null, null, null, null, null],
    SP: [null, null, null, null, null],
    RP: [null, null, null, null, null],
});

/** Build slot card from picker modal (raw = CardInventory) */
const slotCardFromInventory = (ci) => {
    if (!ci?.Card) return null;
    return {
        inventoryID: ci.inventoryID,
        cardID: ci.Card.cardID,
        lineupOrder: null,
        Card: { ...ci.Card, CardInventory: ci },
        CardVariation: ci.CardVariation || null,
    };
};

/** Build slot card from edit mode (raw = UserTeamLineup with CardInventory included) */
const slotCardFromLineup = (entry) => {
    if (!entry?.Card) return null;
    const inventory = entry.CardInventory || null;
    return {
        inventoryID: entry.inventoryID,
        cardID: entry.cardID,
        lineupOrder: entry.lineupOrder ?? null,
        Card: { ...entry.Card, CardInventory: inventory },
        CardVariation: inventory?.CardVariation || null,
    };
};

const getSlotCard = (lineup, pos, idx) => {
    if (idx !== undefined) return lineup[pos]?.[idx] ?? null;
    return lineup[pos] ?? null;
};

const setSlotCard = (lineup, pos, idx, card) => {
    const next = { ...lineup };
    if (idx !== undefined) {
        next[pos] = [...(next[pos] || [])];
        next[pos][idx] = card;
    } else {
        next[pos] = card;
    }
    return next;
};

/** Single pass over lineup to derive usedPlayerIDs and teamScore */
const deriveLineupStats = (lineup) => {
    const playerIDs = new Set();
    let totalRating = 0;
    let count = 0;

    for (const value of Object.values(lineup)) {
        const entries = Array.isArray(value) ? value : value ? [value] : [];
        for (const e of entries) {
            if (!e?.Card) continue;
            if (e.Card.playerID) playerIDs.add(e.Card.playerID);
            if (e.Card.rating) { totalRating += e.Card.rating; count++; }
        }
    }

    return {
        usedPlayerIDs: playerIDs,
        teamScore: count > 0 ? Math.round(totalRating / count) : 0,
    };
};

const TeamBuilderPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const existingTeam = location.state?.team || null;
    const isEditMode = Boolean(existingTeam);

    const [teamName, setTeamName] = useState(existingTeam?.name || '');
    const [lineup, setLineup] = useState(createEmptyLineup);
    const [pickerSlot, setPickerSlot] = useState(null); // { pos, idx }
    const [saving, setSaving] = useState(false);
    const [filling, setFilling] = useState(false);

    const activeConstraints = useMemo(() => {
        const raw = searchParams.get('constraints');
        if (!raw) return null;
        try { return JSON.parse(raw); } catch { return null; }
    }, [searchParams]);

    const series = useSeries();
    const config = useConfig();
    const seriesYears = useMemo(
        () => [...new Set(series.map(s => s.year).filter(Boolean))].sort((a, b) => b - a),
        [series],
    );
    const [teamOptions, setTeamOptions] = useState([]);
    const [filterYear, setFilterYear] = useState('');
    const [filterTeams, setFilterTeams] = useState([]);
    const [filterRating, setFilterRating] = useState('');

    useEffect(() => {
        fetchUserCardTeams()
            .then(raw => {
                const deduped = [...new Map(raw.map(t => [getLastTeamAbbrev(t), t])).entries()]
                    .sort(([a], [b]) => a.localeCompare(b));
                setTeamOptions(deduped);
            })
            .catch(() => {});
    }, []);

    // When constraints set a maxRarity, pre-fill the rating filter
    useEffect(() => {
        if (!activeConstraints || !config) return;
        const maxRarity = activeConstraints.find(c => c.type === 'maxRarity');
        if (maxRarity?.rarity) {
            const cap = config.rarityDisplay?.[maxRarity.rarity]?.rating;
            if (cap) setFilterRating(String(cap));
        }
    }, [activeConstraints, config]);

    // When constraints set teamOnly, pre-fill the team filter.
    // Constraints store modern abbreviations (NYY, CWS) but teamOptions
    // values are raw Lahman codes (NYA, CHA). Translate via teamOptions.
    useEffect(() => {
        if (!activeConstraints || teamOptions.length === 0) return;
        const teamOnly = activeConstraints.find(c => c.type === 'teamOnly');
        if (teamOnly?.teamNameIDs?.length) {
            const rawForModern = new Map(teamOptions.map(([display, raw]) => [display, raw]));
            const rawCodes = teamOnly.teamNameIDs
                .map(modern => rawForModern.get(modern))
                .filter(Boolean);
            setFilterTeams(rawCodes);
        }
    }, [activeConstraints, teamOptions]);

    const cardFilters = useMemo(() => ({
        year: filterYear,
        team: filterTeams,
        rating: filterRating,
    }), [filterYear, filterTeams, filterRating]);

    // Keep ref in sync for stable callbacks
    const pickerSlotRef = useRef(pickerSlot);
    pickerSlotRef.current = pickerSlot;

    // Single derivation pass — usedPlayerIDs + teamScore
    const { usedPlayerIDs, teamScore } = useMemo(() => deriveLineupStats(lineup), [lineup]);

    // Track which batting orders (1-9) are already assigned
    const usedBattingOrders = useMemo(() => {
        const orders = new Map();
        for (const pos of BATTING_POSITIONS) {
            const card = lineup[pos];
            if (card?.lineupOrder) orders.set(card.lineupOrder, pos);
        }
        return orders;
    }, [lineup]);

    // Picker modal: exclude the current slot's player so you can re-select them
    const pickerUsedIDs = useMemo(() => {
        if (!pickerSlot) return usedPlayerIDs;
        const currentCard = getSlotCard(lineup, pickerSlot.pos, pickerSlot.idx);
        const currentPlayerID = currentCard?.Card?.playerID;
        if (!currentPlayerID) return usedPlayerIDs;
        const filtered = new Set(usedPlayerIDs);
        filtered.delete(currentPlayerID);
        return filtered;
    }, [usedPlayerIDs, pickerSlot, lineup]);

    const handleBattingOrderChange = useCallback((pos, newOrder) => {
        setLineup(prev => {
            const next = { ...prev };
            if (newOrder) {
                for (const otherPos of BATTING_POSITIONS) {
                    if (otherPos !== pos && next[otherPos]?.lineupOrder === newOrder) {
                        next[otherPos] = { ...next[otherPos], lineupOrder: prev[pos]?.lineupOrder || null };
                        break;
                    }
                }
            }
            next[pos] = next[pos] ? { ...next[pos], lineupOrder: newOrder || null } : null;
            return next;
        });
    }, []);

    // Load existing team lineup into grid for edit mode
    useEffect(() => {
        if (!existingTeam?.UserTeamLineups) return;

        const newLineup = createEmptyLineup();
        const arrayCounters = { SP: 0, RP: 0, Bench: 0 };

        for (const entry of existingTeam.UserTeamLineups) {
            const { position } = entry;
            const ci = slotCardFromLineup(entry);
            if (!ci) continue;

            if (['SP', 'RP', 'Bench'].includes(position)) {
                const idx = arrayCounters[position];
                if (idx < 5) {
                    newLineup[position][idx] = ci;
                    arrayCounters[position]++;
                }
            } else {
                newLineup[position] = ci;
            }
        }
        setLineup(newLineup);
    }, [existingTeam]);

    // Stable callback — reads pickerSlot from ref
    const handleSelectCard = useCallback((ci) => {
        const slot = pickerSlotRef.current;
        if (!slot) return;
        setLineup(prev => setSlotCard(prev, slot.pos, slot.idx, slotCardFromInventory(ci)));
        setPickerSlot(null);
    }, []);

    const handleClearSlot = useCallback((pos, idx) => {
        setLineup(prev => setSlotCard(prev, pos, idx, null));
    }, []);

    const handleFillBest = useCallback(async () => {
        setFilling(true);
        try {
            const serverParams = { sort: 'rating', dir: 'DESC' };
            if (filterYear) serverParams.year = filterYear;
            if (filterRating) serverParams.maxRating = filterRating;
            if (filterTeams.length > 0) serverParams.teams = filterTeams.join(',');

            let allCards = activeConstraints
                ? await getEligibleCards(activeConstraints)
                : (await fetchCardsByUserIDPaginated(serverParams)).cards || [];

            if (activeConstraints) {
                allCards = allCards.filter(ci => {
                    const card = ci.Card;
                    if (filterYear && card.year !== parseInt(filterYear)) return false;
                    if (filterRating && card.rating > parseInt(filterRating)) return false;
                    if (filterTeams.length > 0 && !filterTeams.some(t => String(card.teamNameID).includes(t))) return false;
                    return true;
                });
            }
            const sortedCards = [...allCards].sort((a, b) => b.Card.rating - a.Card.rating);

            const newLineup = createEmptyLineup();
            const usedIDs = new Set();

            const findBest = (position) => {
                return sortedCards.find(({ Card }) => {
                    if (usedIDs.has(Card.playerID)) return false;
                    if (position === 'DH' || position === 'Bench') {
                        return !Card.positions.includes('SP') && !Card.positions.includes('RP');
                    } else if (['LF', 'CF', 'RF'].includes(position)) {
                        return Card.positions.includes('OF');
                    } else {
                        return Card.positions.includes(position);
                    }
                });
            };

            const assign = (position, idx, ci) => {
                if (!ci) return;
                const normalized = slotCardFromInventory(ci);
                if (!normalized) return;
                usedIDs.add(normalized.Card.playerID);
                if (idx !== undefined) {
                    newLineup[position][idx] = normalized;
                } else {
                    newLineup[position] = normalized;
                }
            };

            FILL_ORDER.forEach(pos => {
                if (['SP', 'RP', 'Bench'].includes(pos)) {
                    for (let i = 0; i < 5; i++) {
                        assign(pos, i, findBest(pos));
                    }
                } else {
                    assign(pos, undefined, findBest(pos));
                }
            });

            // Auto-assign batting order by rating
            const battingCards = BATTING_POSITIONS
                .filter(pos => newLineup[pos]?.Card)
                .map(pos => ({ pos, rating: newLineup[pos].Card.rating }))
                .sort((a, b) => b.rating - a.rating);

            battingCards.forEach((item, i) => {
                newLineup[item.pos] = { ...newLineup[item.pos], lineupOrder: i + 1 };
            });

            setLineup(newLineup);
        } catch (err) {
            console.error('Auto-fill failed:', err);
            toast.error('Failed to load cards for auto-fill');
        } finally {
            setFilling(false);
        }
    }, [activeConstraints, filterYear, filterRating, filterTeams]);

    const handleSubmit = useCallback(async () => {
        if (!teamName.trim()) {
            toast.error('Please enter a team name');
            return;
        }

        setSaving(true);
        try {
            const lineupInfo = {};
            const usedIDs = new Set();

            for (const [position, value] of Object.entries(lineup)) {
                if (!ALLOWED_FIELDS.has(position)) continue;
                const entries = Array.isArray(value) ? value : value ? [value] : [];
                const mapped = entries
                    .filter(e => e?.inventoryID)
                    .map(e => {
                        const playerID = e.Card?.playerID;
                        if (playerID && usedIDs.has(playerID)) {
                            throw new Error(`${e.Card.name} is already in your lineup. Each player can only appear once.`);
                        }
                        if (playerID) usedIDs.add(playerID);
                        return {
                            cardID: e.cardID || e.Card?.cardID,
                            inventoryID: e.inventoryID,
                            lineupOrder: e.lineupOrder ? parseInt(e.lineupOrder, 10) : null,
                        };
                    });

                if (Array.isArray(value)) {
                    lineupInfo[position] = mapped;
                } else if (mapped.length > 0) {
                    lineupInfo[position] = mapped[0];
                }
            }

            // Auto-fill missing batting orders
            const usedOrders = new Set();
            BATTING_POSITIONS.forEach(pos => {
                if (lineupInfo[pos]?.lineupOrder) usedOrders.add(lineupInfo[pos].lineupOrder);
            });

            const needOrder = BATTING_POSITIONS
                .filter(pos => lineupInfo[pos]?.cardID && !lineupInfo[pos]?.lineupOrder)
                .map(pos => ({ pos, rating: lineup[pos]?.Card?.rating || 0 }))
                .sort((a, b) => b.rating - a.rating);

            let nextOrder = 1;
            needOrder.forEach(item => {
                while (usedOrders.has(nextOrder)) nextOrder++;
                lineupInfo[item.pos].lineupOrder = nextOrder;
                usedOrders.add(nextOrder);
                nextOrder++;
            });

            const payload = { score: teamScore, name: teamName.trim(), lineup: lineupInfo };
            if (existingTeam?.userTeamID) payload.userTeamID = existingTeam.userTeamID;

            if (isEditMode) {
                await editUserTeamWithLineup(payload);
            } else {
                await createUserTeamWithLineup(payload);
            }

            navigate('/team/user', {
                state: { message: `Team ${teamName} successfully ${isEditMode ? 'updated' : 'created'}!` },
            });
        } catch (error) {
            const msg = error.response?.data?.error || error.message || 'Error saving team';
            toast.error(msg);
        } finally {
            setSaving(false);
        }
    }, [teamName, lineup, teamScore, isEditMode, existingTeam, navigate]);

    const renderSlot = (pos, idx) => {
        const card = getSlotCard(lineup, pos, idx);
        const label = idx !== undefined ? `${pos} ${idx + 1}` : pos;
        const isBattingPos = BATTING_POSITIONS.includes(pos) && idx === undefined;

        return (
            <div className="tb-slot-outer">
                <span className="tb-slot__pos-label">{label}</span>
                <div
                    className={card ? 'tb-slot tb-slot--filled' : 'tb-slot tb-slot--empty'}
                    onClick={() => setPickerSlot({ pos, idx })}
                >
                    {card ? (
                        <Card
                            card={card.Card}
                            variation={card.CardVariation || null}
                            showLabel
                            hoverFlip
                        />
                    ) : (
                        <span className="tb-slot__plus">+</span>
                    )}
                </div>
                {isBattingPos ? (
                    <select
                        className="tb-slot__order-select"
                        value={card?.lineupOrder || ''}
                        disabled={!card}
                        onClick={e => e.stopPropagation()}
                        onChange={e => handleBattingOrderChange(pos, parseInt(e.target.value) || null)}
                    >
                        <option value="">—</option>
                        {[1,2,3,4,5,6,7,8,9].map(n => {
                            const takenBy = usedBattingOrders.has(n) && usedBattingOrders.get(n) !== pos
                                ? usedBattingOrders.get(n) : null;
                            return (
                                <option key={n} value={n}>
                                    {n}{takenBy ? ` (${takenBy})` : ''}
                                </option>
                            );
                        })}
                    </select>
                ) : null}
            </div>
        );
    };

    return (
        <div className="team-builder">
            <div className="banner-header">
                <BackButton to="/team/user" />
                <h1>{isEditMode ? 'Edit Team' : 'Create Team'}</h1>
            </div>

            <TeamBuilderFilters
                config={config}
                filterYear={filterYear}
                setFilterYear={setFilterYear}
                filterRating={filterRating}
                setFilterRating={setFilterRating}
                filterTeams={filterTeams}
                setFilterTeams={setFilterTeams}
                seriesYears={seriesYears}
                teamOptions={teamOptions}
            />

            <div className="tb-controls">
                <div className="tb-name-input">
                    <label htmlFor="team-name">Team Name</label>
                    <input
                        id="team-name"
                        type="text"
                        value={teamName}
                        onChange={e => setTeamName(e.target.value)}
                        placeholder="Enter team name"
                    />
                </div>
                <div className="tb-score">
                    <label>Rating</label>
                    <span className="tb-score__value">{teamScore}</span>
                </div>
                <div className="tb-actions">
                    <button
                        type="button"
                        className="boring_button"
                        onClick={handleFillBest}
                        disabled={filling || saving}
                    >
                        {filling ? 'Filling...' : 'Fill Best Lineup'}
                    </button>
                    <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={saving || !teamName.trim()}
                    >
                        {saving ? 'Saving...' : isEditMode ? 'Update Team' : 'Create Team'}
                    </button>
                </div>
            </div>

            <div className="tb-grid">
                {POSITION_ROWS.map(row => (
                    <div key={row.label} className="tb-row">
                        <div className="tb-row__slots">
                            {row.slots.map((slot, i) => (
                                <div key={`${slot.pos}-${slot.idx ?? 'single'}-${i}`} className="tb-slot-wrapper">
                                    {renderSlot(slot.pos, slot.idx)}
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>

            {pickerSlot && (
                <PositionPickerModal
                    position={pickerSlot.pos}
                    usedPlayerIDs={pickerUsedIDs}
                    hasCurrent={Boolean(getSlotCard(lineup, pickerSlot.pos, pickerSlot.idx))}
                    onSelect={handleSelectCard}
                    onRemove={() => {
                        handleClearSlot(pickerSlot.pos, pickerSlot.idx);
                        setPickerSlot(null);
                    }}
                    onClose={() => setPickerSlot(null)}
                    extraFilters={cardFilters}
                />
            )}
        </div>
    );
};

export default TeamBuilderPage;
